import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { Container, Header, Segment, Divider, Icon, List, Grid, Card, Message, Table, Step } from 'semantic-ui-react';
import './Playbook.css';

class SkillsLeadership extends Component {
    render() {
        return (
            <Container className="playbook-section-container">
                <h1 className="ui icon center aligned header playbook-branded-header" style={{fontFamily: 'Montserrat', textTransform: 'uppercase', color: 'rgb(35, 37, 58)', fontWeight: 600, fontSize: '38px', marginBottom: '-9px'}}>
                    <i aria-hidden="true" className="icon">
                        <img src="/static/media/logo-icon.7bb8994723bbdcd674f1.png" alt="logo-icon" style={{height: '68px', marginBottom: '-15px'}} />
                    </i>
                    Skills, Organizations & Leadership
                </h1>
                <div className="ui divider"></div>

                <Segment className="playbook-content-segment">
                    <Header as="h2" color="purple">Compensation Plans</Header>
                    
                    <p>
                        This is a very broad topic with many ways to approach it. There are all sorts of producers, from lead generators to account 
                        managers to new account hunters. As a salesperson, we want to get paid for conducting and fulfilling the right activity. 
                        Those activities can change for us based on our role and/or based on the needs of the company.
                    </p>

                    <Message warning size="large">
                        <Message.Header>Critical Principle</Message.Header>
                        <p>
                            The thing every organization, regardless of size, needs to understand and clearly state to its sales team is that 
                            <strong> COMP PLANS ARE YEAR-TO-YEAR AND THEY WILL CHANGE.</strong>
                        </p>
                    </Message>

                    <p>
                        Compensation plans drive behavior and activity. There are a multitude of reasons why compensation plans change. In some years, 
                        an organization will need to address a client-concentration issue and, as such, will heavily incent new work from new customers. 
                        However, new customers can put pressure on the organization. The easiest dollar to get is the next dollar from the current customer.
                    </p>

                    <Header as="h3">Multi-Faceted Compensation Structure</Header>
                    <p>Based on this information, we like compensation plans that give the sales team member multiple ways to "win". To do this, you can do all of the following:</p>

                    <Grid stackable columns={3}>
                        <Grid.Column>
                            <Card color="green">
                                <Card.Content>
                                    <Card.Header>1. Revenue Goals</Card.Header>
                                    <Card.Description>
                                        Pay for revenue acquisition based on a goal. This includes a tiered percentage of payout based on how close 
                                        the salesperson gets to target.
                                    </Card.Description>
                                </Card.Content>
                            </Card>
                        </Grid.Column>

                        <Grid.Column>
                            <Card color="blue">
                                <Card.Content>
                                    <Card.Header>2. New Client Bonus</Card.Header>
                                    <Card.Description>
                                        Pay a flat fee for new client acquisition to encourage expansion of the customer base.
                                    </Card.Description>
                                </Card.Content>
                            </Card>
                        </Grid.Column>

                        <Grid.Column>
                            <Card color="orange">
                                <Card.Content>
                                    <Card.Header>3. Team Bonus</Card.Header>
                                    <Card.Description>
                                        Pay a discretionary, team-based bonus if the company hits its targets to promote collaboration.
                                    </Card.Description>
                                </Card.Content>
                            </Card>
                        </Grid.Column>
                    </Grid>

                    <p>
                        Using a multifaceted comp plan gives the salesperson a chance to make money throughout the year rather than only on a 
                        percentage of the sale value. Plus, the team-based bonus can promote collaboration across the team.
                    </p>

                    <Segment color="yellow">
                        <Header as="h4">Keep It Manageable</Header>
                        <p>
                            The key is to keep it manageable. Comp plans can easily become very complicated and ultimately unmanageable. We would say 
                            keep it simple, but nothing about managing people and their pay is simple. So we will go with manageable.
                        </p>
                    </Segment>

                    <Header as="h4">How Much Should a Salesperson Make?</Header>
                    <p>
                        The answer is "whatever the market rate is." When it comes to compensation, sales team members should have uncapped potential. 
                        But to start, the sales team member will have:
                    </p>
                    <List numbered>
                        <List.Item>A base salary</List.Item>
                        <List.Item>A variable pay structure</List.Item>
                    </List>

                    <p>
                        He or she also has a market value based on a number of factors (location, experience, etc.). The company should define what 
                        the salesperson should make (comp + variable) if he or she meets goals exactly. Then the model should include accelerators 
                        if the salesperson exceeds those goals.
                    </p>

                    <Message info>
                        <Message.Header>Uncapped Potential</Message.Header>
                        <p>
                            Not having accelerators and/or uncapped potential can cause salespeople to govern their sales once they meet goals. But 
                            if they have an opportunity to "juice" their earnings while the company exceeds financial expectations, then everyone will win.
                        </p>
                    </Message>

                    <Divider />

                    <Header as="h2" color="purple">Sales Organizational Structures</Header>
                    
                    <p>
                        When creating a sales organization, there are multiple ways to structure it. Some are very simple where you have a "salesperson" 
                        who does everything from lead gen to closing and who reports to the CEO.
                    </p>

                    <p>
                        In other organizations, you have roles with layer(s) of management. In other words, this is not a "one size fits all" process. 
                        The key is to create an organizational structure that not only works but is also based on a career path. It's not just a pure 
                        organizational structure.
                    </p>

                    <Header as="h3">Career Progression Path</Header>
                    <Step.Group stackable="tablet" widths={4}>
                        <Step>
                            <Icon name="search" />
                            <Step.Content>
                                <Step.Title>Lead Generation</Step.Title>
                                <Step.Description>Beginning role focused on creating new conversations</Step.Description>
                            </Step.Content>
                        </Step>
                        
                        <Step>
                            <Icon name="handshake" />
                            <Step.Content>
                                <Step.Title>Sales</Step.Title>
                                <Step.Description>Customer-based sales with varying responsibilities</Step.Description>
                            </Step.Content>
                        </Step>
                        
                        <Step>
                            <Icon name="user tie" />
                            <Step.Content>
                                <Step.Title>Account Management</Step.Title>
                                <Step.Description>Requires more experience managing stakeholders</Step.Description>
                            </Step.Content>
                        </Step>
                        
                        <Step>
                            <Icon name="users" />
                            <Step.Content>
                                <Step.Title>Leadership</Step.Title>
                                <Step.Description>Managing teams and organizational growth</Step.Description>
                            </Step.Content>
                        </Step>
                    </Step.Group>

                    <p>
                        In general, the progression for a salesperson is to begin with lead generation and then move to customer-based sales where 
                        the roles and responsibilities can change based on the company and the products, solutions, etc., that are to be sold.
                    </p>

                    <p>
                        In short, you want to set your team members up to be successful in their career paths. To do that, you want to equip them 
                        with the skills they need to be successful.
                    </p>

                    <Divider />

                    <Header as="h3" color="purple">Words of Caution</Header>
                    
                    <Message negative size="large">
                        <Message.Header>Critical Management Mistake</Message.Header>
                        <p>
                            If you think your best salesperson will automatically be the best manager because he or she sells the best, you are 
                            mistaken. That could be true, or it could be a PAINFUL mistake to make because it creates a multiplying expense in 
                            the following ways:
                        </p>
                    </Message>

                    <Grid stackable columns={2}>
                        <Grid.Column>
                            <Card color="red">
                                <Card.Content>
                                    <Card.Header>Lost Production</Card.Header>
                                    <Card.Description>
                                        You lose your top producer as he or she is now managing and not out selling.
                                    </Card.Description>
                                </Card.Content>
                            </Card>
                        </Grid.Column>

                        <Grid.Column>
                            <Card color="red">
                                <Card.Content>
                                    <Card.Header>Ineffective Management</Card.Header>
                                    <Card.Description>
                                        You don't have an effective manager leading your team, and the team is not producing at max output.
                                    </Card.Description>
                                </Card.Content>
                            </Card>
                        </Grid.Column>
                    </Grid>

                    <Segment color="orange">
                        <Header as="h4">Industry Statistics</Header>
                        <p>
                            Furthermore, the employment duration of a Chief Revenue Officer is 2 years on average. There are many reasons for this, 
                            but one of the driving factors of the short stints is that many times the CRO role is filled with a person who is great 
                            at sales but lacks the skills to manage.
                        </p>
                    </Segment>

                    <Header as="h3">Building Verifiable Skills</Header>
                    <p>
                        As a leader, whether you are hiring for a new role and/or growing a team from within, you want to create an organization 
                        that builds verifiable skills that will make your team successful. And as a salesperson, you want to be part of an organization 
                        that sets you up for success by defining the skills you need that accompany the activities you need to execute in order to 
                        maximize your sales capabilities.
                    </p>

                    <p>That's where the skills matrix comes in. The matrix gives a clear definition of roles and the expected activities and skills to create success in each role. And it also creates the opportunity for managers to interject objectivity combined with job performance results into the promotion process.</p>

                    <Header as="h4">Skills Matrix Benefits</Header>
                    <p>This won't be a perfect match but is a very good method for you to think through to build out your organizational structure and how you will set your team members up for success by:</p>
                    
                    <List numbered>
                        <List.Item><strong>Equipping them to be successful</strong> - Providing the tools and knowledge needed</List.Item>
                        <List.Item><strong>Positioning them to be successful</strong> - Placing them in roles that match their skills</List.Item>
                        <List.Item><strong>Seeing them achieve personal gain that creates corporate growth</strong> - Aligning individual and company success</List.Item>
                    </List>

                    <Divider />

                    <Header as="h3" color="purple">Sample Skills Matrix and Career Path</Header>
                    
                    <p>
                        Below is a comprehensive framework for understanding the skills and responsibilities at each level of a sales organization. 
                        This matrix helps both leaders and team members understand expectations and career progression paths.
                    </p>

                    <Table celled structured>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell rowSpan='2'>Role Level</Table.HeaderCell>
                                <Table.HeaderCell colSpan='3'>Core Competencies</Table.HeaderCell>
                                <Table.HeaderCell rowSpan='2'>Key Responsibilities</Table.HeaderCell>
                            </Table.Row>
                            <Table.Row>
                                <Table.HeaderCell>Technical Skills</Table.HeaderCell>
                                <Table.HeaderCell>Communication</Table.HeaderCell>
                                <Table.HeaderCell>Leadership</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            <Table.Row>
                                <Table.Cell><strong>Lead Generator</strong></Table.Cell>
                                <Table.Cell>
                                    <List bulleted size="small">
                                        <List.Item>CRM proficiency</List.Item>
                                        <List.Item>Email/LinkedIn outreach</List.Item>
                                        <List.Item>Basic qualification</List.Item>
                                    </List>
                                </Table.Cell>
                                <Table.Cell>
                                    <List bulleted size="small">
                                        <List.Item>Phone skills</List.Item>
                                        <List.Item>Written communication</List.Item>
                                        <List.Item>Active listening</List.Item>
                                    </List>
                                </Table.Cell>
                                <Table.Cell>
                                    <List bulleted size="small">
                                        <List.Item>Self-management</List.Item>
                                        <List.Item>Goal orientation</List.Item>
                                    </List>
                                </Table.Cell>
                                <Table.Cell>
                                    Generate qualified leads, schedule meetings, maintain CRM accuracy
                                </Table.Cell>
                            </Table.Row>
                            
                            <Table.Row>
                                <Table.Cell><strong>Sales Representative</strong></Table.Cell>
                                <Table.Cell>
                                    <List bulleted size="small">
                                        <List.Item>Product knowledge</List.Item>
                                        <List.Item>Demo capabilities</List.Item>
                                        <List.Item>Proposal writing</List.Item>
                                    </List>
                                </Table.Cell>
                                <Table.Cell>
                                    <List bulleted size="small">
                                        <List.Item>Meeting control</List.Item>
                                        <List.Item>Objection handling</List.Item>
                                        <List.Item>Presentation skills</List.Item>
                                    </List>
                                </Table.Cell>
                                <Table.Cell>
                                    <List bulleted size="small">
                                        <List.Item>Initiative</List.Item>
                                        <List.Item>Persistence</List.Item>
                                        <List.Item>Collaboration</List.Item>
                                    </List>
                                </Table.Cell>
                                <Table.Cell>
                                    Manage full sales cycle, conduct demos, negotiate terms, close deals
                                </Table.Cell>
                            </Table.Row>
                            
                            <Table.Row>
                                <Table.Cell><strong>Account Manager</strong></Table.Cell>
                                <Table.Cell>
                                    <List bulleted size="small">
                                        <List.Item>Industry expertise</List.Item>
                                        <List.Item>Strategic planning</List.Item>
                                        <List.Item>Contract management</List.Item>
                                    </List>
                                </Table.Cell>
                                <Table.Cell>
                                    <List bulleted size="small">
                                        <List.Item>Stakeholder management</List.Item>
                                        <List.Item>Executive presence</List.Item>
                                        <List.Item>Conflict resolution</List.Item>
                                    </List>
                                </Table.Cell>
                                <Table.Cell>
                                    <List bulleted size="small">
                                        <List.Item>Relationship building</List.Item>
                                        <List.Item>Influence without authority</List.Item>
                                        <List.Item>Cross-functional collaboration</List.Item>
                                    </List>
                                </Table.Cell>
                                <Table.Cell>
                                    Manage key accounts, drive expansion, ensure customer success and retention
                                </Table.Cell>
                            </Table.Row>
                            
                            <Table.Row>
                                <Table.Cell><strong>Sales Manager</strong></Table.Cell>
                                <Table.Cell>
                                    <List bulleted size="small">
                                        <List.Item>Analytics and reporting</List.Item>
                                        <List.Item>Process optimization</List.Item>
                                        <List.Item>Technology implementation</List.Item>
                                    </List>
                                </Table.Cell>
                                <Table.Cell>
                                    <List bulleted size="small">
                                        <List.Item>Coaching skills</List.Item>
                                        <List.Item>Performance management</List.Item>
                                        <List.Item>Team communication</List.Item>
                                    </List>
                                </Table.Cell>
                                <Table.Cell>
                                    <List bulleted size="small">
                                        <List.Item>Team building</List.Item>
                                        <List.Item>Performance coaching</List.Item>
                                        <List.Item>Strategic thinking</List.Item>
                                    </List>
                                </Table.Cell>
                                <Table.Cell>
                                    Lead sales team, develop talent, ensure team quota attainment, report to leadership
                                </Table.Cell>
                            </Table.Row>
                        </Table.Body>
                    </Table>

                    <Divider />

                    <Header as="h3" color="purple">Leadership Development Principles</Header>
                    
                    <Grid stackable columns={2}>
                        <Grid.Column>
                            <Card color="green">
                                <Card.Content>
                                    <Card.Header>Successful Leadership Traits</Card.Header>
                                    <Card.Description>
                                        <List bulleted>
                                            <List.Item>Clear communication of expectations</List.Item>
                                            <List.Item>Consistent coaching and feedback</List.Item>
                                            <List.Item>Data-driven decision making</List.Item>
                                            <List.Item>Team development focus</List.Item>
                                            <List.Item>Process improvement mindset</List.Item>
                                        </List>
                                    </Card.Description>
                                </Card.Content>
                            </Card>
                        </Grid.Column>

                        <Grid.Column>
                            <Card color="red">
                                <Card.Content>
                                    <Card.Header>Common Leadership Pitfalls</Card.Header>
                                    <Card.Description>
                                        <List bulleted>
                                            <List.Item>Micromanaging team members</List.Item>
                                            <List.Item>Avoiding difficult conversations</List.Item>
                                            <List.Item>Focusing only on top performers</List.Item>
                                            <List.Item>Neglecting process documentation</List.Item>
                                            <List.Item>Inconsistent application of standards</List.Item>
                                        </List>
                                    </Card.Description>
                                </Card.Content>
                            </Card>
                        </Grid.Column>
                    </Grid>

                    <Segment color="purple">
                        <Header as="h4">Key Management Philosophy</Header>
                        <p>
                            The best sales managers understand that their job is not to be the best salesperson on the team, but to create an 
                            environment where every team member can perform at their highest level. This requires a different skill set than 
                            individual selling.
                        </p>
                    </Segment>

                    <Header as="h4">Transition from Individual Contributor to Manager</Header>
                    <p>When promoting someone from an individual contributor role to management, ensure they understand the fundamental shift:</p>
                    
                    <Table celled>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>As Individual Contributor</Table.HeaderCell>
                                <Table.HeaderCell>As Manager</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            <Table.Row>
                                <Table.Cell>Success measured by personal quota attainment</Table.Cell>
                                <Table.Cell>Success measured by team quota attainment</Table.Cell>
                            </Table.Row>
                            <Table.Row>
                                <Table.Cell>Focus on individual relationships and deals</Table.Cell>
                                <Table.Cell>Focus on team development and process improvement</Table.Cell>
                            </Table.Row>
                            <Table.Row>
                                <Table.Cell>Direct control over outcomes</Table.Cell>
                                <Table.Cell>Influence outcomes through others</Table.Cell>
                            </Table.Row>
                            <Table.Row>
                                <Table.Cell>Individual skill development</Table.Cell>
                                <Table.Cell>Team skill development and coaching</Table.Cell>
                            </Table.Row>
                        </Table.Body>
                    </Table>

                    <Message success>
                        <Message.Header>Building a High-Performance Culture</Message.Header>
                        <p>
                            The goal is to create a sales organization where success is predictable and repeatable, not dependent on individual 
                            heroics. This requires clear processes, consistent training, objective measurements, and ongoing development of both 
                            individual contributors and leaders.
                        </p>
                    </Message>

                    <Divider />

                    <Header as="h3" color="purple">Conclusion</Header>
                    <p>
                        Building a successful sales organization requires thoughtful consideration of compensation structures, clear career progression 
                        paths, and proper development of leadership skills. The organizations that invest in these foundational elements create 
                        sustainable competitive advantages through their people.
                    </p>

                    <p>
                        Remember that great salespeople are not automatically great managers, and great managers are not always the best individual 
                        contributors. Each role requires different skills, and successful organizations recognize and develop these differences rather 
                        than assuming they transfer automatically.
                    </p>

                    <Segment color="teal">
                        <Header as="h4">Final Thought</Header>
                        <p>
                            The most successful sales organizations are those that create clear expectations, provide ongoing development opportunities, 
                            and align individual success with organizational success. This requires intentional design and consistent execution of 
                            people development practices.
                        </p>
                    </Segment>
                </Segment>
            </Container>
        );
    }
}

export default observer(SkillsLeadership);